import React, { useState, useEffect } from 'react';
import { Box, Container, Button, Typography, ButtonGroup, IconButton, Paper } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store';
import { setCurrentSlide, updateCursorPosition, toggleCursorControl } from '../store/presentationSlice';
import { onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import ScriptingSidebar from './ScriptingSidebar';
import IntroductionSlide from './slides/IntroductionSlide';
import CreditAnalysisSlide from './slides/CreditAnalysisSlide';
import InterestCalculatorSlide from './slides/InterestCalculatorSlide';
import TermOptionsSlide from './slides/TermOptionsSlide';
import LogisticsSlide from './slides/LogisticsSlide';
import ProgramSummarySlide from './slides/ProgramSummarySlide';
import AdditionalInfoSlide from './slides/AdditionalInfoSlide';
import CursorControl from './CursorControl';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import { createPresentationSession } from '../services/presentationService';

const PresentationView: React.FC = () => {
  const dispatch = useDispatch();
  const presentationState = useSelector((state: RootState) => state.presentation);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [clientUrl, setClientUrl] = useState<string | null>(null);
  const [connectedClients, setConnectedClients] = useState(0);

  useEffect(() => {
    const createSession = async () => {
      setIsCreatingSession(true);
      try {
        const sessionId = await createPresentationSession(presentationState);
        setSessionId(sessionId);
        setClientUrl(window.location.origin + '/client/' + sessionId);
      } catch (error) {
        console.error('Error creating session:', error);
      } finally {
        setIsCreatingSession(false);
      }
    };

    createSession();
  }, []);

  // Sync presentation state to Firebase
  useEffect(() => {
    if (sessionId) {
      console.log('Syncing to Firebase:', { hoveredProgramStep: presentationState.hoveredProgramStep });
      updateDoc(doc(db, 'presentations', sessionId), {
        currentSlide: presentationState.currentSlide,
        clientName: presentationState.clientName,
        selectedTerms: presentationState.selectedTerms,
        additionalPayment: presentationState.additionalPayment,
        hoveredProgramStep: presentationState.hoveredProgramStep,
        calculations: presentationState.calculations,
        cursorControlEnabled: presentationState.cursorControlEnabled
      });
    }
  }, [
    sessionId, 
    presentationState.currentSlide,
    presentationState.clientName,
    presentationState.selectedTerms,
    presentationState.additionalPayment,
    presentationState.hoveredProgramStep,
    presentationState.calculations,
    presentationState.cursorControlEnabled
  ]);

  // Sync cursor position to Firebase with throttling
  useEffect(() => {
    if (sessionId && presentationState.cursorControlEnabled) {
      const throttledUpdate = setTimeout(() => {
        updateDoc(doc(db, 'presentations', sessionId), {
          cursorPosition: presentationState.cursorPosition,
        });
      }, 16); // Approximately 60fps

      return () => clearTimeout(throttledUpdate);
    }
  }, [sessionId, presentationState.cursorPosition, presentationState.cursorControlEnabled]);

  // Listen for updates including connected clients
  useEffect(() => {
    if (sessionId) {
      const unsubscribe = onSnapshot(doc(db, 'presentations', sessionId), (doc) => {
        const data = doc.data();
        if (data) {
          // Only update slide if it's different from current state
          if (data.currentSlide !== presentationState.currentSlide) {
            dispatch(setCurrentSlide(data.currentSlide));
          }
          if (data.cursorPosition && presentationState.cursorControlEnabled) {
            dispatch(updateCursorPosition(data.cursorPosition));
          }
          // Update connected clients count
          setConnectedClients(data.connectedClients || 0);
        }
      });

      return () => unsubscribe();
    }
  }, [sessionId, dispatch, presentationState.cursorControlEnabled, presentationState.currentSlide]);

  const handleSlideChange = (slideIndex: number) => {
    // Immediately update local state
    dispatch(setCurrentSlide(slideIndex));
    
    // Update Firebase if session exists
    if (sessionId) {
      updateDoc(doc(db, 'presentations', sessionId), {
        currentSlide: slideIndex
      }).catch(error => {
        console.error('Error updating slide:', error);
      });
    }
  };

  const handlePrevSlide = () => {
    handleSlideChange(Math.max(0, presentationState.currentSlide - 1));
  };

  const handleNextSlide = () => {
    handleSlideChange(Math.min(6, presentationState.currentSlide + 1));
  };

  const copyClientLink = () => {
    if (clientUrl) {
      navigator.clipboard.writeText(clientUrl);
    }
  };

  const renderCurrentSlide = () => {
    switch (presentationState.currentSlide) {
      case 0:
        return <IntroductionSlide />;
      case 1:
        return <CreditAnalysisSlide />;
      case 2:
        return <InterestCalculatorSlide />;
      case 3:
        return <TermOptionsSlide />;
      case 4:
        return <LogisticsSlide />;
      case 5:
        return <ProgramSummarySlide isPresenter={true} />;
      case 6:
        return <AdditionalInfoSlide />;
      default:
        return <IntroductionSlide />;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#F0F2F5' }}>
      <Container 
        maxWidth={false} 
        sx={{ 
          py: 4,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          maxWidth: '1100px',
          ml: 4,
          mr: 'auto',
          bgcolor: '#F0F2F5'
        }}
      >
        <Box sx={{ 
          mb: 4,
          position: 'relative',
          zIndex: 1
        }}>
          <Box sx={{ 
            mb: 2, 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            position: 'relative',
            zIndex: 2
          }}>
            <ButtonGroup 
              variant="contained" 
              color="primary" 
              sx={{ 
                position: 'relative',
                zIndex: 2
              }}
            >
              <Button
                onClick={handlePrevSlide}
                disabled={presentationState.currentSlide === 0}
              >
                Previous
              </Button>
              <Button
                onClick={handleNextSlide}
                disabled={presentationState.currentSlide === 6}
              >
                Next
              </Button>
            </ButtonGroup>

            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2,
              position: 'relative',
              zIndex: 2
            }}>
              {isCreatingSession ? (
                <Typography>Creating session...</Typography>
              ) : clientUrl ? (
                <>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {connectedClients === 1 ? '1 Client Connected' : `${connectedClients} Clients Connected`}
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={copyClientLink}
                  >
                    Copy Client Link
                  </Button>
                </>
              ) : null}
              <IconButton
                sx={{ ml: 2 }}
                onClick={() => setSidebarOpen(true)}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Paper 
              elevation={3} 
              className="presentation-container"
              sx={{ 
                p: 3, 
                minHeight: '80vh',
                position: 'relative',
                flex: 1,
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              {renderCurrentSlide()}
              <CursorControl isPresenter={true} />
            </Paper>
          </Box>
        </Box>
      </Container>

      <ScriptingSidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <Box sx={{ position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 2 }}>
        <IconButton
          onClick={handlePrevSlide}
          disabled={presentationState.currentSlide === 0}
          sx={{
            bgcolor: 'background.paper',
            '&:hover': {
              bgcolor: 'grey.100',
            },
          }}
        >
          <NavigateBeforeIcon />
        </IconButton>
        <IconButton
          onClick={handleNextSlide}
          disabled={presentationState.currentSlide === 6}
          sx={{
            bgcolor: 'background.paper',
            '&:hover': {
              bgcolor: 'grey.100',
            },
          }}
        >
          <NavigateNextIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

export default PresentationView;
